/**
 * ServiceCertificatesSubTab: read-only list of clinical service
 * certificates issued for this property.
 *
 * Pulled into the Services tab on 2026-05-29 so the Services area
 * groups "the work done on the property" in one place (invoices for
 * the commercial side, certificates for the clinical side).
 *
 * Read-only for now. The revoke flow and audit details still live on
 * the Documents tab until that tab is reshaped in a follow-up pass.
 *
 * Data load mirrors the cert section of DocumentsTab: cert_submissions
 * joined to cert_certificates to surface the public certificate
 * number, plus a service_log_number lookup for the cross-link.
 */

import { useCallback, useEffect, useState } from 'react';
import { formatDateMedium } from '../../../../../../utils/formatters';
import type { SupabaseClient } from '@supabase/supabase-js';
import { StatusBadge } from '../../../../../../components/DataTable';
import type { EnrichedProperty } from '../types';

interface CertRow {
  id: string;
  service_date: string | null;
  invoice_number: string | null;
  operator_name: string | null;
  submission_status: string;
  authorized_at: string | null;
  primary_asset_description: string | null;
  service_log_id: string | null;
  cert_id: string | null;
  certificate_number: string | null;
  certificate_status: string | null;
  service_log_number: string | null;
}

interface ServiceCertificatesSubTabProps {
  supabase: SupabaseClient;
  property: EnrichedProperty;
}

function formatDate(s: string | null | undefined): string {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  return formatDateMedium(d);
}

const STATUS_STYLES: Record<string, { bg: string; color: string; label: string }> = {
  authorized: { bg: 'var(--color-success-bg)', color: 'var(--color-success-text)', label: 'Authorised' },
  verified: { bg: 'var(--color-warning-bg)', color: 'var(--color-warning-text)', label: 'Verified' },
  certified: { bg: 'var(--color-warning-bg)', color: 'var(--color-warning-text)', label: 'Certified' },
  revoked: { bg: 'var(--color-error-bg)', color: 'var(--color-error-text)', label: 'Revoked' },
};

export default function ServiceCertificatesSubTab({ supabase, property }: ServiceCertificatesSubTabProps) {
  const [rows, setRows] = useState<CertRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: subs, error: subErr } = await supabase
        .from('cert_submissions')
        .select('id, service_date, invoice_number, operator_name, submission_status, authorized_at, primary_asset_description, service_log_id')
        .eq('property_id', property.id)
        .order('service_date', { ascending: false })
        .limit(200);
      if (subErr) throw subErr;
      const certRows = (subs as Omit<CertRow, 'cert_id' | 'certificate_number' | 'certificate_status' | 'service_log_number'>[] | null) ?? [];
      const visible = certRows.filter((c) => ['certified', 'verified', 'authorized'].includes(c.submission_status));

      const subIds = visible.map((c) => c.id);
      const serviceLogIds = visible.map((c) => c.service_log_id).filter((x): x is string => !!x);

      const [certNumRes, logRes] = await Promise.all([
        subIds.length > 0
          ? supabase
              .from('cert_certificates')
              .select('id, submission_id, certificate_number, certificate_status')
              .in('submission_id', subIds)
          : Promise.resolve({ data: [] as Array<{ id: string; submission_id: string; certificate_number: string; certificate_status: string }> }),
        serviceLogIds.length > 0
          ? supabase
              .from('service_logs')
              .select('id, service_log_number')
              .in('id', serviceLogIds)
          : Promise.resolve({ data: [] as Array<{ id: string; service_log_number: string }> }),
      ]);

      const certByRow = new Map<string, { cert_id: string; certificate_number: string; certificate_status: string }>();
      ((certNumRes.data ?? []) as Array<{ id: string; submission_id: string; certificate_number: string; certificate_status: string }>).forEach((r) => {
        if (r.submission_id) {
          certByRow.set(r.submission_id, { cert_id: r.id, certificate_number: r.certificate_number, certificate_status: r.certificate_status });
        }
      });
      const logByRow = new Map<string, string>();
      ((logRes.data ?? []) as Array<{ id: string; service_log_number: string }>).forEach((r) => {
        logByRow.set(r.id, r.service_log_number);
      });

      const enriched: CertRow[] = visible.map((c) => {
        const info = certByRow.get(c.id);
        return {
          ...c,
          cert_id: info?.cert_id ?? null,
          certificate_number: info?.certificate_number ?? null,
          certificate_status: info?.certificate_status ?? null,
          service_log_number: c.service_log_id ? logByRow.get(c.service_log_id) ?? null : null,
        };
      });

      setRows(enriched);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load service certificates');
    } finally {
      setLoading(false);
    }
  }, [supabase, property.id]);

  useEffect(() => { void load(); }, [load]);

  if (loading) return <div className="text-muted fs-sm">Loading service certificates…</div>;
  if (error) return <div className="alert alert-error">{error}</div>;
  if (rows.length === 0) {
    return (
      <div className="text-muted fs-sm" style={{ padding: '1.5rem 0' }}>
        No service certificates issued for this property yet.
      </div>
    );
  }

  return (
    <div>
      <p className="text-muted fs-sm mb-md">
        Service certificates issued from completed services. Revoke and audit details remain available on the Documents tab for now.
      </p>
      <div className="card" style={{ overflowX: 'auto' }}>
        <table className="data-table">
          <thead>
            <tr>
              <th>Certificate</th>
              <th>Service date</th>
              <th>Operator</th>
              <th>Asset</th>
              <th>Invoice</th>
              <th>Status</th>
              <th>Authorised</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const statusKey = r.certificate_status === 'revoked' ? 'revoked' : r.submission_status;
              const cfg = STATUS_STYLES[statusKey] ?? { bg: 'var(--color-border)', color: 'var(--color-sidebar-hover)', label: r.submission_status };
              return (
                <tr key={r.id}>
                  <td>
                    <span className="ref-code">{r.certificate_number ?? '—'}</span>
                  </td>
                  <td>{formatDate(r.service_date)}</td>
                  <td>{r.operator_name ?? '—'}</td>
                  <td>{r.primary_asset_description ?? '—'}</td>
                  <td>
                    <span className="ref-code">{r.invoice_number ?? '—'}</span>
                  </td>
                  <td>
                    <StatusBadge bg={cfg.bg} color={cfg.color}>{cfg.label}</StatusBadge>
                  </td>
                  <td>{formatDate(r.authorized_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
